/*
  Injects the `bundle_note` block's rich text into the Recharge bundles widget.

  The widget's markup belongs to Recharge, so there is no theme-side slot to
  render into. This element holds a hidden copy of the merchant's content and,
  once the widget has mounted, clones it in directly after the configured
  target (`.recharge-bundle-variant-size-title` by default).

  Timing is the whole problem the delay solves: the widget is mounted by the
  app's own script, well after the theme's DOM is parsed. The merchant-set
  delay is the first attempt; if the widget still is not there, a debounced
  MutationObserver picks it up whenever it does arrive, and keeps the note in
  place when the widget re-renders (variant or bundle-size change wipes its
  subtree, taking our clone with it).
*/
(() => {
  const DEFAULT_SELECTOR = '.recharge-bundle-variant-size-title';
  const DEFAULT_DELAY = 800;
  const RESCAN_THROTTLE = 200;

  if (customElements.get('product-bundle-note')) return;

  /*
    Recharge mounts some widgets into a shadow root. A light-DOM query is the
    common case and cheap, so it runs first; the recursive walk only happens
    when that fails, and the root it finds is cached so later rescans go back
    to being a single querySelectorAll.
  */
  const queryLight = (root, selector) => Array.from(root.querySelectorAll(selector));

  const queryDeep = (root, selector, found = []) => {
    found.push(...queryLight(root, selector));
    if (found.length) return found;

    for (const element of root.querySelectorAll('*')) {
      if (element.shadowRoot) {
        queryDeep(element.shadowRoot, selector, found);
        if (found.length) return found;
      }
    }

    return found;
  };

  class ProductBundleNote extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('[data-bundle-note-template]');
      if (!this.template) return;

      this.selector = this.dataset.target || DEFAULT_SELECTOR;
      this.delay = Number.parseInt(this.dataset.delay, 10);
      if (Number.isNaN(this.delay)) this.delay = DEFAULT_DELAY;
      this.injectAll = this.dataset.placement === 'all';
      this.noteId = `bundle-note-${this.dataset.blockId || 'default'}`;
      this.injected = [];
      this.searchRoot = null;

      this.onMutation = this.onMutation.bind(this);
      this.timer = setTimeout(() => this.start(), this.delay);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.rescanTimer);
      this.observer?.disconnect();
      // Section re-render in the theme editor: drop clones so they cannot double up.
      this.injected.forEach((node) => node.remove());
      this.injected = [];
    }

    start() {
      this.inject();

      this.observer = new MutationObserver(this.onMutation);
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    onMutation() {
      // Our own insert triggers the observer, so bail while every clone is still mounted.
      if (this.injected.length && this.injected.every((node) => node.isConnected)) return;

      clearTimeout(this.rescanTimer);
      this.rescanTimer = setTimeout(() => this.inject(), RESCAN_THROTTLE);
    }

    findTargets() {
      const root = this.searchRoot || document;
      let targets = queryLight(root, this.selector);

      if (!targets.length && !this.searchRoot) {
        targets = queryDeep(document, this.selector);
        if (targets.length) {
          const node = targets[0].getRootNode();
          this.searchRoot = node === document ? null : node;
        }
      }

      return this.injectAll ? targets : targets.slice(0, 1);
    }

    inject() {
      const targets = this.findTargets();
      if (!targets.length) return;

      this.injected = this.injected.filter((node) => node.isConnected);

      targets.forEach((target) => {
        const next = target.nextElementSibling;
        if (next && next.dataset.bundleNoteFor === this.noteId) return;

        const note = this.template.cloneNode(true);
        note.removeAttribute('hidden');
        note.removeAttribute('data-bundle-note-template');
        note.dataset.bundleNoteFor = this.noteId;

        target.insertAdjacentElement('afterend', note);
        this.injected.push(note);
      });
    }
  }

  customElements.define('product-bundle-note', ProductBundleNote);
})();
